import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  STORY_MAIN_IDEA_MAX_LENGTH,
  fitSpreadText,
  sceneReferencesFit,
  textBudgetFor,
  textFitRefused,
  textWords,
  type BookAttributes,
} from '@kitup/book-schema';
import { useEffect, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { BookDto } from '../api/books.js';
import { adminAccountsApi } from '../api/admin-accounts.js';
import { isApiError, userErrorMessage } from '../api/errors.js';
import { storyApi, type SceneDto, type StoryDraftDto } from '../api/story.js';
import { TextArea } from '../components/Fields.js';
import { FormMessage } from '../components/FormMessage.js';
import { TextFitHint } from './TextFitHint.js';
import { illustrationsApi } from '../api/illustrations.js';
import { useI18n } from '../i18n/context.js';
import { useAuth } from '../auth/context.js';

/**
 * Шаг «Сюжет»: человек пишет пожелания одним текстом, раскладку по разворотам
 * делает ИИ, текст разворотов остаётся правимым — до применения в черновике,
 * после применения прямо в сценах книги. Промпты картинок под капотом.
 */
export function StoryStep({
  book,
  pendingCharacters,
  billingExempt = false,
}: {
  readonly book: BookDto;
  /** An administrator's redraws cost nothing, as on the illustrations step. */
  readonly billingExempt?: boolean;
  /**
   * Names of the book's characters not yet approved in its style — approval needs none —
   * or null while the cast is loading.
   */
  readonly pendingCharacters: readonly string[] | null;
}): JSX.Element {
  const { t, formatDate } = useI18n();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  /** `null` — поле ещё не трогали, показываем бриф последнего черновика. */
  const [wishes, setWishes] = useState<string | null>(null);
  const [selectedDraftId, setSelectedDraftId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [unapplying, setUnapplying] = useState(false);

  const scenesQuery = useQuery({
    queryKey: ['book-scenes', book.id],
    queryFn: () => storyApi.scenes(book.id),
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (scene) => scene.promptStatus === 'queued' || scene.promptStatus === 'running',
      )
        ? 2_000
        : false,
  });
  const draftsQuery = useQuery({
    queryKey: ['story-drafts', book.id],
    queryFn: () => storyApi.drafts(book.id),
    refetchInterval: (query) =>
      query.state.data?.items.some(
        (draft) =>
          draft.status === 'queued' ||
          draft.status === 'running' ||
          draft.parsed?.some(
            (spread) => spread.promptStatus === 'queued' || spread.promptStatus === 'running',
          ),
      )
        ? 2_000
        : false,
  });

  // Only for what a redraw would cost, said before a text edit stales a drawn picture.
  const illustrations = useQuery({
    queryKey: ['book-illustrations', book.id],
    queryFn: () => illustrationsApi.list(book.id),
    enabled: scenesQuery.data?.items.some((scene) => scene.imageAssetId) ?? false,
  });

  const generate = useMutation({
    mutationFn: (mainIdea: string) => storyApi.generate(book.id, { mainIdea }),
    onMutate: () => {
      setActionError(null);
      setActionMessage(null);
    },
    onSuccess: async ({ draftId }) => {
      setSelectedDraftId(draftId);
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: ['story-drafts', book.id] });
    },
    // Ошибки бизнес-логики API уже содержат безопасное человекочитаемое
    // объяснение. Оно важнее общего «что-то пошло не так»: например, сервер
    // прямо подсказывает утвердить всех героев перед генерацией сюжета.
    onError: (error) =>
      setActionError(
        isApiError(error) && error.message.trim() ? error.message : userErrorMessage(error, t),
      ),
  });

  const refreshScenes = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['book-scenes', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['story-drafts', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['books'] }),
      queryClient.invalidateQueries({ queryKey: ['book-characters', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-things', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-locations', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-illustrations', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-cover', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book-preview', book.id] }),
    ]);
  };

  const saveDraftText = async (
    draftId: string,
    index: number,
    textRu: string,
    regeneratePrompt = false,
  ): Promise<boolean> => {
    setActionError(null);
    try {
      await storyApi.updateDraft(draftId, {
        spreads: [{ index, textRu, ...(regeneratePrompt ? { regeneratePrompt: true } : {}) }],
      });
      await queryClient.invalidateQueries({ queryKey: ['story-drafts', book.id] });
      return true;
    } catch (error: unknown) {
      setActionError(userErrorMessage(error, t));
      return false;
    }
  };

  const saveSceneText = async (
    sceneId: string,
    textRu: string,
    regeneratePrompt = false,
  ): Promise<boolean> => {
    setActionError(null);
    try {
      await storyApi.updateScene(sceneId, {
        textRu,
        ...(regeneratePrompt ? { regeneratePrompt: true } : {}),
      });
      await refreshScenes();
      return true;
    } catch (error: unknown) {
      setActionError(userErrorMessage(error, t));
      return false;
    }
  };

  const applyDraft = async (draft: StoryDraftDto, coverTitle: string): Promise<void> => {
    setApplying(true);
    setApplyError(null);
    try {
      await storyApi.apply(draft.id, coverTitle);
    } catch (error: unknown) {
      if (
        !isApiError(error) ||
        error.code !== 'story_replace_requires_confirmation' ||
        !window.confirm(t.story.ai.replaceConfirm)
      ) {
        setApplyError(userErrorMessage(error, t));
        setApplying(false);
        return;
      }
      await storyApi.apply(draft.id, coverTitle, true);
    }
    await refreshScenes();
    setApplying(false);
    setSearchParams({ step: 'illustrations' }, { replace: true });
  };

  const unapplyStory = async (): Promise<void> => {
    if (!window.confirm(t.story.ai.adminUnapplyConfirm)) return;
    setUnapplying(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const result = await adminAccountsApi.unapplyStory(book.id);
      setSelectedDraftId(result.draftId);
      await refreshScenes();
      setActionMessage(t.story.ai.adminUnapplied);
    } catch (error: unknown) {
      setActionError(userErrorMessage(error, t));
    } finally {
      setUnapplying(false);
    }
  };

  if (scenesQuery.isPending || draftsQuery.isPending) return <p>{t.common.loading}</p>;
  if (scenesQuery.isError || draftsQuery.isError)
    return <p role="alert">{t.common.unexpectedError}</p>;

  const drafts = draftsQuery.data.items;
  const scenes = scenesQuery.data.items;
  const selectedDraft = drafts.find((draft) => draft.id === selectedDraftId) ?? drafts[0];
  const appliedDraft = drafts.find((draft) => draft.isApplied);
  const showHistory = !appliedDraft && drafts.length > 1;
  const running = drafts.some((draft) => draft.status === 'queued' || draft.status === 'running');
  const wishesValue = wishes ?? briefWishes(drafts[0]);
  const wishesTooLong = wishesValue.length > STORY_MAIN_IDEA_MAX_LENGTH;

  return (
    <section>
      <h1 className="text-2xl font-semibold">{t.story.title}</h1>
      <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.story.subtitle}</p>

      {actionError ? (
        <div className="mt-4">
          <FormMessage kind="error">{actionError}</FormMessage>
        </div>
      ) : null}
      {actionMessage ? (
        <p role="status" className="mt-4 text-sm text-emerald-700">
          {actionMessage}
        </p>
      ) : null}

      {user?.role === 'admin' && appliedDraft ? (
        <div className="mt-4 rounded-xl border border-violet-300 bg-violet-50 p-4">
          <p className="text-sm text-violet-900">{t.story.ai.adminUnapplyHint}</p>
          <button
            type="button"
            className="kitup-secondary-button mt-3"
            disabled={unapplying}
            onClick={() => void unapplyStory()}
          >
            {unapplying ? t.story.ai.adminUnapplying : t.story.ai.adminUnapply}
          </button>
        </div>
      ) : null}

      {/* In the narrow builder column the draft history sits under the story, not beside it. */}
      <div className="mt-6 grid gap-7">
        <div>
          {appliedDraft ? (
            <p className="text-sm text-[var(--kitup-color-muted)]">{t.story.ai.approvedLocked}</p>
          ) : (
            <>
              <TextArea
                label={t.story.ai.fields.wishes}
                hint={t.story.ai.fields.wishesHint}
                value={wishesValue}
                rows={6}
                onChange={setWishes}
              />
              <p
                className={`mt-1 text-right text-xs ${
                  wishesTooLong ? 'font-semibold text-red-700' : 'text-[var(--kitup-color-muted)]'
                }`}
              >
                {t.story.ai.fields.wishesCounter(wishesValue.length, STORY_MAIN_IDEA_MAX_LENGTH)}
              </p>
              {wishesTooLong ? (
                <div className="mt-2">
                  <FormMessage kind="error">
                    {t.story.ai.fields.wishesTooLong(STORY_MAIN_IDEA_MAX_LENGTH)}
                  </FormMessage>
                </div>
              ) : null}
              <p className="mt-3 text-sm text-[var(--kitup-color-muted)]">{t.story.ai.intro}</p>
              {/* The server fixes the plan with the first draft, not with approval: say it here. */}
              {drafts.length === 0 && !book.paidAt ? (
                <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">
                  {t.story.ai.generateLocksPlan}
                </p>
              ) : null}
              <button
                type="button"
                className="kitup-primary-button mt-5"
                // An empty idea still wrote a story — a random one, at the cost of a generation.
                disabled={generate.isPending || running || wishesTooLong || !wishesValue.trim()}
                onClick={() => generate.mutate(wishesValue.trim())}
              >
                {generate.isPending || running
                  ? t.story.ai.generating
                  : drafts.length > 0
                    ? t.story.ai.regenerate
                    : t.story.ai.generate}
              </button>
            </>
          )}

          {!appliedDraft && selectedDraft && !selectedDraft.isApplied ? (
            <DraftPreview
              draft={selectedDraft}
              applying={applying}
              applyError={applyError}
              pendingCharacters={pendingCharacters}
              attributes={book.attributes}
              onApply={(coverTitle) => void applyDraft(selectedDraft, coverTitle)}
              onSaveText={(index, textRu, regeneratePrompt) =>
                saveDraftText(selectedDraft.id, index, textRu, regeneratePrompt)
              }
            />
          ) : scenes.length > 0 ? (
            <CurrentStory
              scenes={scenes}
              attributes={book.attributes}
              redrawsLeft={
                billingExempt
                  ? null
                  : // An unpaid book's free picture is redrawn only after paying (402), even
                    // with redos unlimited.
                    !book.paidAt
                    ? 'payment'
                    : illustrations.data?.replacements.unlimited
                      ? null
                      : illustrations.data?.replacements.remaining
              }
              applied={Boolean(appliedDraft)}
              onSaveText={saveSceneText}
            />
          ) : null}
        </div>

        {showHistory ? (
          <aside>
            <h2 className="font-semibold">{t.story.ai.history}</h2>
            <ol className="mt-3 grid gap-2">
              {drafts.map((draft) => (
                <li key={draft.id}>
                  <button
                    type="button"
                    className="kitup-choice-card w-full text-left"
                    aria-pressed={draft.id === selectedDraft?.id}
                    onClick={() => setSelectedDraftId(draft.id)}
                  >
                    <span className="grid gap-0.5">
                      <span>{t.story.ai.draftDate(formatDate(draft.createdAt))}</span>
                      <small className="text-[var(--kitup-color-muted)]">
                        {t.story.ai.status[draft.status]}
                      </small>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>
        ) : null}
      </div>
    </section>
  );
}

function DraftPreview({
  draft,
  applying,
  applyError,
  pendingCharacters,
  attributes,
  onApply,
  onSaveText,
}: {
  readonly draft: StoryDraftDto;
  readonly applying: boolean;
  readonly applyError: string | null;
  readonly pendingCharacters: readonly string[] | null;
  readonly attributes: BookAttributes;
  onApply(coverTitle: string): void;
  onSaveText(index: number, textRu: string, regeneratePrompt?: boolean): Promise<boolean>;
}): JSX.Element {
  const { t } = useI18n();
  const promptsReady = draft.parsed?.every((spread) => spread.promptStatus === 'ready') ?? false;
  const referencesReady = draft.parsed?.every((spread) => sceneReferencesFit(spread)) ?? false;
  const [coverTitle, setCoverTitle] = useState(draft.titleRu ?? '');

  useEffect(() => setCoverTitle(draft.titleRu ?? ''), [draft.id, draft.titleRu]);

  return (
    <section className="mt-8 border-t border-[var(--kitup-color-border)] pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t.story.ai.preview}</h2>
        <span className="text-sm text-[var(--kitup-color-muted)]">
          {t.story.ai.status[draft.status]}
        </span>
      </div>
      {draft.status === 'failed' ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {t.story.ai.failed}
        </p>
      ) : null}
      {draft.warnings.map((warning) => (
        <p key={warning.characterId} role="status" className="mt-3 text-sm text-amber-800">
          {t.story.ai.underused(warning.characterName, warning.sceneCount)}
        </p>
      ))}
      {draft.parsed ? (
        <div className="mt-4 grid gap-3">
          <div className="grid max-w-xl gap-1.5">
            <label htmlFor="story-cover-title" className="text-sm font-medium">
              {t.story.ai.coverTitle}
            </label>
            <input
              id="story-cover-title"
              className="kitup-input"
              value={coverTitle}
              maxLength={200}
              required
              aria-describedby="story-cover-title-hint"
              onChange={(event) => setCoverTitle(event.target.value)}
            />
            <span id="story-cover-title-hint" className="text-xs text-[var(--kitup-color-muted)]">
              {t.story.ai.coverTitleHint}
            </span>
          </div>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.story.ai.editHint}</p>
          {draft.parsed.map((spread) => (
            <SpreadText
              key={spread.index}
              index={spread.index}
              text={spread.textRu}
              // A draft's spreads land in the book's default layout.
              place={{ layoutId: null, bandImageRatio: null, textBlocks: [] }}
              attributes={attributes}
              promptStatus={spread.promptStatus}
              promptError={spread.promptError}
              onSave={(textRu, regeneratePrompt) =>
                onSaveText(spread.index, textRu, regeneratePrompt)
              }
            />
          ))}
          {/* Said before the click: afterwards the cast is fixed. */}
          <p className="mt-2 text-sm text-[var(--kitup-color-muted)]">{t.story.ai.applyLocks}</p>
          {/*
            What blocks approval is said where the button is, before the click: the server's
            refusal used to land at the top of the page, a long scroll above a button that
            seemed to do nothing.
          */}
          {pendingCharacters && pendingCharacters.length > 0 ? (
            <div role="status" className="grid justify-items-start gap-2">
              <p className="text-sm text-amber-800">
                {t.story.ai.applyNeedsCharacters(pendingCharacters.join(', '))}
              </p>
              <Link to="?step=characters" className="kitup-secondary-button">
                {t.builder.goToStep(t.builder.steps.characters)}
              </Link>
            </div>
          ) : null}
          <button
            type="button"
            className="kitup-primary-button w-fit"
            disabled={
              applying ||
              !promptsReady ||
              !referencesReady ||
              !coverTitle.trim() ||
              pendingCharacters === null ||
              pendingCharacters.length > 0
            }
            onClick={() => onApply(coverTitle.trim())}
          >
            {t.story.ai.apply}
          </button>
          {!referencesReady ? (
            <FormMessage kind="error">{t.story.ai.referenceLimit}</FormMessage>
          ) : null}
          {applyError ? <FormMessage kind="error">{applyError}</FormMessage> : null}
        </div>
      ) : null}
    </section>
  );
}

/** Развороты, которые уже лежат в книге: правка текста идёт прямо в сцены. */
function CurrentStory({
  scenes,
  attributes,
  redrawsLeft,
  applied,
  onSaveText,
}: {
  readonly scenes: readonly SceneDto[];
  readonly attributes: BookAttributes;
  /** null — redraws cost nothing here; undefined — not known yet; 'payment' — unpaid. */
  readonly redrawsLeft: number | null | undefined | 'payment';
  readonly applied: boolean;
  onSaveText(sceneId: string, textRu: string, regeneratePrompt?: boolean): Promise<boolean>;
}): JSX.Element {
  const { t } = useI18n();
  return (
    <section className="mt-8 border-t border-[var(--kitup-color-border)] pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t.story.ai.current}</h2>
        {applied ? (
          <span className="text-sm text-[var(--kitup-color-muted)]">{t.story.ai.applied}</span>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.story.ai.currentHint}</p>
      <div className="mt-4 grid gap-3">
        {scenes.map((scene) => (
          <SpreadText
            key={scene.id}
            index={scene.index}
            text={scene.textRu}
            place={scene}
            attributes={attributes}
            // A text edit marks a drawn picture stale; one already stale has nothing to lose.
            stale={scene.imageAssetId && !scene.imageStale ? { remaining: redrawsLeft } : undefined}
            promptStatus={scene.promptStatus}
            promptError={scene.promptError}
            onSave={(textRu, regeneratePrompt) => onSaveText(scene.id, textRu, regeneratePrompt)}
          />
        ))}
      </div>
    </section>
  );
}

/** Текст одного разворота: правка с явным сохранением, без промпта картинки. */
function SpreadText({
  index,
  text,
  place,
  attributes,
  stale,
  promptStatus,
  promptError,
  onSave,
}: {
  readonly index: number;
  readonly text: string;
  /** Where the text is printed: the renderer's measure needs the layout and the blocks. */
  readonly place: Pick<SceneDto, 'layoutId' | 'bandImageRatio' | 'textBlocks'>;
  readonly attributes: BookAttributes;
  /** Saving new words stales the drawn picture; `remaining` is what a redraw costs. */
  readonly stale?: { readonly remaining: number | null | undefined | 'payment' };
  readonly promptStatus: 'queued' | 'running' | 'ready' | 'failed';
  readonly promptError: string | null;
  onSave(textRu: string, regeneratePrompt?: boolean): Promise<boolean>;
}): JSX.Element {
  const { t } = useI18n();
  const [value, setValue] = useState(text);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => setValue(text), [text]);

  const save = async (): Promise<void> => {
    if (
      stale &&
      // The API's question: new words stale the picture, a line break alone does not.
      textWords(value) !== textWords(text) &&
      !window.confirm(t.story.ai.staleConfirm(stale.remaining))
    )
      return;
    setSaving(true);
    const ok = await onSave(value.trim(), promptStatus === 'failed');
    setSaving(false);
    setSaved(ok);
  };

  // What is sent is the trimmed text, so edges alone are nothing to save.
  const changed = value.trim() !== text.trim();
  const spread = { layoutId: place.layoutId, bandImageRatio: place.bandImageRatio };
  // As the API: saving the text lays it out afresh, and is measured against the text before.
  const fit = fitSpreadText({ ...spread, textRu: value.trim() }, attributes);
  const tooLong = textFitRefused(
    fit,
    fitSpreadText({ ...spread, textRu: text, textBlocks: place.textBlocks }, attributes),
  );
  const target = textBudgetFor(
    place.layoutId ?? attributes.layout.default,
    attributes,
    undefined,
    place,
  ).perSpread;
  const showSave = changed || promptStatus === 'failed' || saving;
  const promptBusy = promptStatus === 'queued' || promptStatus === 'running';

  return (
    <article className="rounded-xl bg-[var(--kitup-color-soft)] p-4">
      <TextArea
        label={t.story.manual.spread(index + 1)}
        value={value}
        rows={4}
        onChange={(next) => {
          setSaved(false);
          setValue(next);
        }}
      />
      <TextFitHint
        fit={fit}
        length={value.trim().length}
        target={target}
        baseSizePt={attributes.typography.baseSizePt}
      />
      {/* An empty row under every spread was a screen of padding on a phone: it shows when it has something. */}
      {showSave || (saved && !changed) || promptBusy ? (
        <div className="mt-3 flex min-h-9 flex-wrap items-center gap-3">
          {/* Eleven greyed "Save text" buttons read as eleven things to do; it appears once there is one. */}
          {showSave ? (
            <button
              type="button"
              className="kitup-secondary-button"
              disabled={saving || value.trim() === '' || tooLong}
              onClick={() => void save()}
            >
              {saving ? t.story.ai.saving : t.story.ai.save}
            </button>
          ) : null}
          {saved && !changed ? (
            <span role="status" className="text-sm text-emerald-700">
              {t.story.ai.saved}
            </span>
          ) : null}
          {promptBusy ? (
            <span role="status" className="text-sm text-[var(--kitup-color-muted)]">
              {t.story.ai.promptUpdating}
            </span>
          ) : null}
          {promptStatus === 'failed' ? (
            <span role="alert" className="text-sm text-red-700">
              {promptError || t.story.ai.promptFailed}
            </span>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function briefWishes(draft?: StoryDraftDto): string {
  const mainIdea = draft?.brief.mainIdea;
  return typeof mainIdea === 'string' ? mainIdea : '';
}
